import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router'
import { z } from 'zod'
import { parseSearchParams } from 'zod-search-params'
import { type Route } from './+types/route'
import { ActionButton } from '~/components/action-button.tsx'
import { ArticleHeader } from '~/components/article-header.tsx'
import { Articles } from '~/components/articles.tsx'
import { Cols } from '~/components/cols.tsx'
import { Empty } from '~/components/empty.tsx'
import { GradientsContainer } from '~/components/gradients-container.tsx'
import { Layout } from '~/components/layout.tsx'
import { PublicationState } from '~/model/api.ts'
import { type Article } from '~/utils/blog'
import { getFormatter } from '~/utils/date-formatter.server.ts'
import { api } from '~/utils/gql-client.server.ts'
import { fallbackLng, supportedLngs } from '~/utils/i18n.ts'
import i18next from '~/utils/i18next.server.ts'
import { generateMeta, type MetaProps } from '~/utils/meta.ts'
import { getPageState } from '~/utils/page-state.ts'

export function meta({ data }: Route.MetaArgs) {
  return generateMeta(data?.metaProps as MetaProps)
}

export let handle = {
  i18n: 'press',
}

export async function loader({ request }: Route.LoaderArgs) {
  const { start } = parseSearchParams(
    z.object({
      start: z.number().optional().catch(0),
    }),
    new URL(request.url).searchParams,
  )

  const { posts } = await api.Posts({
    sort: 'publishedAt:DESC',
    limit: 10,
    start: start ?? 0,
    where: {
      category: 'PRESS',
    },
    publicationState: PublicationState.Live,
    locale: 'en',
  })

  const formatter = await getFormatter(request)

  const { locale } = await getPageState(request)

  const t = await i18next.getFixedT(locale ?? fallbackLng, 'press')
  const metaProps: MetaProps = {
    seo: {
      metaTitle: t('press_meta_title'),
      metaDescription: t('press_meta_description'),
    },
    localizations: supportedLngs,
    pageState: await getPageState(request),
  }

  return {
    metaProps: metaProps,
    posts: posts.map((post) => ({
      id: post.id,
      title: post.title,
      perex: post.perex,
      link: `/press/${post.slug}`,
      cover: {
        url: post.cover.data?.attributes?.url ?? null,
        alt: post.cover.data?.attributes?.alternativeText ?? null,
      },
      dateDisplay: post.publishedAt
        ? formatter.format(new Date(post.publishedAt))
        : undefined,
    })) as Article[],
  }
}

export default function Press({ loaderData }: Route.ComponentProps) {
  const { t } = useTranslation(['press', 'common'])
  const { posts: loaderPosts } = loaderData
  const [posts, setPosts] = useState(loaderPosts)
  const [loading, setLoading] = useState(false)
  const [endReached, setEndReached] = useState(false)
  const fetcher = useFetcher<typeof loader>()

  useEffect(() => {
    if (!fetcher.data || fetcher.state === 'loading') {
      return
    }

    if (fetcher.data) {
      const newPosts = fetcher.data.posts
      setLoading(false)

      if (!newPosts || newPosts.length === 0) {
        setEndReached(true)
        return
      }
      setPosts((prevPosts) => [...prevPosts, ...newPosts])
    }
  }, [loaderPosts, fetcher.data, fetcher.state])

  const loadMore = async () => {
    setLoading(true)
    const start = posts.length
    const query = `?start=${start}`
    await fetcher.load(query)
  }

  return (
    <Layout>
      <ArticleHeader
        title={t('press', {
          ns: 'press',
        })}
      />
      <GradientsContainer>
        <div className="container">
          <Cols size="10">
            <div className="py-16">
              {posts.length === 0 && <Empty message={t('press_posts_empty')} />}
              {posts.length > 0 && (
                <>
                  <Articles articles={posts} />
                  <div className="pt-16 text-center">
                    {!endReached && (
                      <ActionButton
                        size="big"
                        onClick={() => loadMore()}
                        state={loading ? 'loading' : undefined}
                      >
                        {t('load_more_button')}
                      </ActionButton>
                    )}
                  </div>
                </>
              )}
            </div>
          </Cols>
        </div>
      </GradientsContainer>
    </Layout>
  )
}
